import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import '../../modalright.css';
import '../../popup.css';
import '../../pricing-strategies.css';
import '../../wallet.css';
import '../../wallet-fixes.css';
import { getDriverApiBaseUrl } from '../../utils/utils';
import {
    formatInt, Person, Pill, StatCard, EmptyState, WalletSpinner,
    useWalletToasts, WalletToastStack, WalletSelect,
} from './walletShared';

const ROLE_META = {
    COLLECTOR: { label: 'Collector', icon: 'fa-money', tone: 'warning', blurb: 'Can collect COD cash from drivers and hold it.' },
    ACCOUNTANT: { label: 'Accountant', icon: 'fa-calculator', tone: 'primary', blurb: 'Receives handovers from supervisors and settles them to the company.' },
};

const ROLE_OPTIONS = [
    { value: 'COLLECTOR', label: 'Collector — collect cash from drivers' },
    { value: 'ACCOUNTANT', label: 'Accountant — receive handovers & settle' },
];

export default function WalletSetup() {
    const API_BASE = `${getDriverApiBaseUrl()}/shypvdriverapp/`;
    const email = sessionStorage.getItem('useremail') || '';
    const authHeaders = () => {
        const t = sessionStorage.getItem('usertoken');
        return t ? { Authorization: `Bearer ${t}` } : {};
    };
    const { toasts, toast, dismiss } = useWalletToasts();

    const [roles, setRoles] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);

    // Assign modal
    const [modal, setModal] = useState(false);
    const [pickUserId, setPickUserId] = useState('');
    const [pickRole, setPickRole] = useState('COLLECTOR');
    const [saving, setSaving] = useState(false);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [r, c] = await Promise.all([
                axios.get(`${API_BASE}admin/wallet/roles`, { headers: authHeaders() }),
                axios.get(`${API_BASE}admin/wallet/roles/candidates`, { headers: authHeaders() }),
            ]);
            setRoles((r.data && r.data.data) || []);
            setCandidates((c.data && c.data.data) || []);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load wallet setup.');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

    const collectors = useMemo(() => roles.filter(r => r.role === 'COLLECTOR'), [roles]);
    const accountants = useMemo(() => roles.filter(r => r.role === 'ACCOUNTANT'), [roles]);

    // Options for the searchable person picker — react-select filters these by
    // label as the user types, so no separate filter/search state is needed.
    const candidateOptions = useMemo(() => candidates.map(c => {
        const alreadyHasRole = (c.walletRoles || []).includes(pickRole);
        return {
            value: c.userId,
            label: `${c.name || c.email || `User #${c.userId}`}${c.email ? ` · ${c.email}` : ''}`,
            name: c.name,
            email: c.email,
            userId: c.userId,
            alreadyHasRole,
        };
    }), [candidates, pickRole]);

    const openAssign = (role) => { setPickRole(role || 'COLLECTOR'); setPickUserId(''); setModal(true); };

    const submitAssign = async () => {
        if (!pickUserId) return toast.error('Pick a person to assign.');
        const person = candidates.find(c => String(c.userId) === String(pickUserId));
        setSaving(true);
        try {
            const res = await axios.post(`${API_BASE}admin/wallet/roles`,
                {
                    userId: person ? person.userId : null,
                    email: person ? person.email : null,
                    personnelName: person ? person.name : null,
                    role: pickRole,
                    actorEmail: email,
                },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
            if (res.data && res.data.success) {
                toast.success(`${person ? (person.name || person.email) : 'User'} is now a ${ROLE_META[pickRole].label}.`);
                setModal(false);
                loadAll();
            } else {
                toast.error((res.data && res.data.errors && res.data.errors.map(e => e.errorMessage || e).join(', ')) || 'Failed to assign.');
            }
        } catch (err) {
            console.error(err);
            toast.error((err.response && err.response.data && err.response.data.errors
                && err.response.data.errors.map(e => e.errorMessage || e).join(', ')) || 'Failed to assign role.');
        } finally { setSaving(false); }
    };

    const revoke = async (r) => {
        if (!window.confirm(`Revoke ${ROLE_META[r.role].label} from ${r.personnelName || r.email}?`)) return;
        try {
            const res = await axios.post(`${API_BASE}admin/wallet/roles/${r.id}/revoke`, {}, { headers: authHeaders() });
            if (res.data && res.data.success) { toast.success('Role revoked.'); loadAll(); }
            else toast.error('Failed to revoke.');
        } catch (err) { console.error(err); toast.error('Failed to revoke role.'); }
    };

    const RoleCard = ({ role, list }) => {
        const m = ROLE_META[role];
        return (
            <div className="w-card">
                <div className="w-card-header">
                    <span className="w-card-title"><i className={`fa ${m.icon}`} aria-hidden="true" /> {m.label}s</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span className="w-card-meta">{formatInt(list.length)}</span>
                        <button className="w-btn w-btn-primary w-btn-sm" onClick={() => openAssign(role)}>
                            <i className="fa fa-plus" aria-hidden="true" /> Add {m.label.toLowerCase()}
                        </button>
                    </div>
                </div>
                <div className="w-card-body">
                    <p style={{ color: '#6b7280', marginTop: 0, fontSize: 13 }}>{m.blurb}</p>
                    {loading ? <WalletSpinner label="Loading…" /> : list.length === 0 ? (
                        <EmptyState icon="fa-user-plus" title={`No ${m.label.toLowerCase()}s yet`} subtitle={`Add someone to grant the ${m.label.toLowerCase()} authority.`} />
                    ) : (
                        <div className="w-rtable">
                            <table className="table table-sm align-middle w-rtable-table" style={{ marginTop: 4, marginBottom: 0 }}>
                                <thead><tr><th>Person</th><th>Role</th><th></th></tr></thead>
                                <tbody>
                                    {list.map(r => (
                                        <tr key={r.id}>
                                            <td data-label="Person"><Person name={r.personnelName || r.email || `User #${r.userId}`} sub={r.email || `#${r.userId}`} /></td>
                                            <td data-label="Role"><Pill tone={m.tone} icon={m.icon}>{m.label}</Pill></td>
                                            <td className="text-end">
                                                <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => revoke(r)}>
                                                    <i className="fa fa-user-times" aria-hidden="true" /> Revoke
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="wallet-page wui">
            <WalletToastStack toasts={toasts} onDismiss={dismiss} />

            <div className="w-topbar">
                <div className="w-topbar-left">
                    <div className="w-icon-badge"><i className="fa fa-sliders" aria-hidden="true" /></div>
                    <div>
                        <h2 className="w-title">Wallet Setup</h2>
                        <p className="w-subtitle">
                            Decide who may <strong>collect cash</strong> from drivers and who is the <strong>accountant</strong> that receives handovers.
                        </p>
                    </div>
                </div>
                <div className="w-topbar-actions">
                    <button className="w-btn w-btn-ghost" onClick={loadAll} disabled={loading}>
                        <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`} aria-hidden="true" /> Refresh
                    </button>
                </div>
            </div>

            <div className="w-stat-grid">
                <StatCard icon="fa-money" label="Collectors" value={formatInt(collectors.length)} tone="warning"
                    sub="Can collect COD cash from drivers" />
                <StatCard icon="fa-calculator" label="Accountants" value={formatInt(accountants.length)} accent
                    sub="Receive handovers &amp; settle to company" />
            </div>

            <RoleCard role="COLLECTOR" list={collectors} />
            <RoleCard role="ACCOUNTANT" list={accountants} />

            {/* Assign modal */}
            {modal && (
                <div className="modal-overlay w-modal">
                    <div className="modal-content" style={{ maxWidth: 520 }}>
                        <div className="w-modal-header">
                            <div className="w-modal-icon"><i className={`fa ${ROLE_META[pickRole].icon}`} aria-hidden="true" /></div>
                            <div style={{ flex: 1 }}>
                                <h3>Assign {ROLE_META[pickRole].label}</h3>
                                <p>{ROLE_META[pickRole].blurb}</p>
                            </div>
                            <button className="close-btn" onClick={() => setModal(false)}><i className="fa fa-times" aria-hidden="true" /></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Role</label>
                                <WalletSelect
                                    options={ROLE_OPTIONS}
                                    value={ROLE_OPTIONS.find(o => o.value === pickRole)}
                                    onChange={(opt) => setPickRole(opt.value)}
                                    isClearable={false}
                                    aria-label="Role" />
                            </div>
                            <div className="form-group">
                                <label>Person</label>
                                <WalletSelect
                                    options={candidateOptions}
                                    value={candidateOptions.find(o => o.value === pickUserId) || null}
                                    onChange={(opt) => setPickUserId(opt ? opt.value : '')}
                                    isOptionDisabled={(opt) => opt.alreadyHasRole}
                                    isClearable
                                    placeholder="Search by name or email…"
                                    noOptionsMessage={() => 'No people match your search.'}
                                    formatOptionLabel={(opt, { context }) => context === 'menu' ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <span style={{ fontWeight: 600 }}>{opt.name || opt.email || `User #${opt.userId}`}</span>
                                            {opt.email && opt.name && <span style={{ fontSize: 11.5, color: 'var(--w-muted)' }}>{opt.email}</span>}
                                            {opt.alreadyHasRole && (
                                                <span style={{ fontSize: 11, color: 'var(--w-warning)', fontWeight: 600 }}>
                                                    <i className="fa fa-check-circle" aria-hidden="true" /> Already {ROLE_META[pickRole].label.toLowerCase()}
                                                </span>
                                            )}
                                        </div>
                                    ) : (opt.name || opt.email || `User #${opt.userId}`)}
                                    aria-label="Person" />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="w-btn w-btn-ghost" onClick={() => setModal(false)} disabled={saving}>Cancel</button>
                            <button className="w-btn w-btn-primary" onClick={submitAssign} disabled={saving || !pickUserId}>
                                {saving ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Saving…</> : <><i className="fa fa-check" aria-hidden="true" /> Assign</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
